import React, { useEffect, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Error } from "../../../../../../models/error";
import { Instruction } from "../../../../../../models/instruction";
import { Searches, Filters, Sorts, Sort, Row } from "../../../../../../models/shipeezi.table";
import InstructionService from "../../../../../../services/instruction.service";
import { InstructionWhen, Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import InstructionModal from "./InstructionModal";
import useStyles from "./styles";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
    changeStatus: boolean,
  }
}

const InstructionPage = (props: Props) => {
  const {permission} = props;
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [instruction, setInstruction] = useState<Instruction>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>();

  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  const { refetch, isFetching } = useQuery(
    ["instruction.list", page, pageSize, filters, searches, sorting],
    () =>
      InstructionService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        sortBy: sorting ? JSON.stringify([...sorting]) : JSON.stringify([["updatedAt", "desc"]]),
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setInstructions(response.data);
      },
    }
  );

  const { refetch: getInstructionTotal } = useQuery(
    ["instruction.total", searches, filters],
    () =>
      InstructionService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data !== null) setTotal(response.data);
        else setTotal(0);
      },
    }
  );

  const { mutate: createInstruction, isLoading: isCreating } = useMutation(
    ["instruction.create"],
    (instruction: Instruction) => InstructionService.create(instruction),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setHasErrors(false);
        refetch();
        getInstructionTotal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const { mutate: updateInstruction, isLoading: isUpdating } = useMutation(
    ["instruction.update"],
    ({ id, instruction }: { id: number; instruction: Instruction }) =>
      InstructionService.update(id, instruction),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"), {
          id: "request.notification.success.instruction",
        });
        setHasErrors(false);
        refetch();
        getInstructionTotal();
      },
      onError: (error: AxiosError<Error>) => {
        if (error && error.response) NotificationToast.error(error?.response?.data?.message);
        setHasErrors(true);
      },
    }
  );

  const editInstruction = (index: number) => {
    const findInstruction = instructions.find(
      (instructionItem) => Number(instructionItem.id) === index
    );

    if (hasErrors !== undefined) setHasErrors(undefined);
    if (findInstruction) {
      setInstruction(findInstruction);
      setOpenModal(true);
    }
  };

  const changeInstructionStatus = (index: number) => {
    const findInstruction = instructions.find(
      (instructionItem) => Number(instructionItem.id) === index
    );

    if (findInstruction) {
      updateInstruction({
        id: Number(index),
        instruction: {
          ...findInstruction,
          status: findInstruction.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sort: Sort[]) => {
    const sorting: Sorts = [];

    sort.forEach((sort) => sorting.push([sort.columnName, sort.direction]));
    setSorting(sorting);
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          customColor={Color.INFO}
          className={classes.changeStatusButton}
          onClick={() => setOpenConfirmation(true)}
          data-cy="changeStatus"
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveInstructionSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, instructions);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "code", title: translate("code") },
    {
      name: "description",
      title: translate("name"),
      formatComponent: (value?: string) => value,
    },
    { name: "when", hideSearch: true, title: translate("when") },
    {
      name: "status",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (status: string) => badgeStatus(status),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord"],
    },
    handleNewRecord: permission && isShowAction(currentUser, permission.create)
      ? {
        text: translate("create"),
        action: () => {
          if (hasErrors !== undefined) setHasErrors(undefined);
          setInstruction(undefined);
          setOpenModal(true);
        },
        tooltip: translate("create"),
      }
      : undefined,
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("when"),
          column: "when",
          options: [InstructionWhen.UPLIFT, InstructionWhen.DELIVERY, InstructionWhen.COORDINATOR],
        },
        { name: translate("code"), column: "code" },
        {
          name: translate("name"),
          column: "description",
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: permission && isShowAction(currentUser, permission.changeStatus)
      ? renderButtonToChangeStatus()
      : undefined,
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="instructionList">
      <ShipeeziNewGrid
        data={instructions}
        columns={columns}
        totalItems={total}
        loadingData={isFetching || isUpdating || isCreating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (instructionId) => editInstruction(Number(instructionId))
            : undefined,
          handleChange: permission && isShowAction(currentUser, permission.changeStatus)
            ? (instructionId) => changeInstructionStatus(Number(instructionId))
            : undefined,
        }}
        positionActionsColumn="right"
        onSorting={handleSort}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveInstructionSelected(rowsArr)}
        generalActions={generalActions}
      />
      {openModal && (
        <InstructionModal
          instruction={instruction}
          open={openModal}
          isLoading={isUpdating || isCreating}
          closeModal={() => setOpenModal(false)}
          hasError={hasErrors}
          submit={(instruction: Instruction) => {
            if (instruction.id) updateInstruction({ id: instruction.id, instruction });
            else {
              createInstruction({
                ...instruction,
                companyId: currentUser?.companyId,
              });
            }
          }}
        />
      )}
      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateInstruction({
                id: rowId,
                instruction: { status } as Instruction,
              });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default InstructionPage;
